import { Checkbox, Form, message, Modal, Select } from 'antd';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import moment from 'moment-timezone';
import TextArea from 'antd/lib/input/TextArea';
import { locationsRestCrudThunksForSupplier } from '../../thunks/locations_thunks';
import { areasRestCrudThunksForSupplier } from '../../thunks/areas_thunks';
import {
	bulkTransferAssetsToLocationsForSupplier,
	cancelScheduledAssetTransfer,
	moveAssetToLocationForSupplier,
	scheduleAssetTransfer,
	updateScheduledAssetTransfer,
} from '../../thunks/assets_thunks';
import OWAsyncSelect from '../ow_async_select/OWAsyncSelect';
import ModalFormFooter from '../common/ModalFormFooter';
import { nullSafeGet, nullSafeGetOrElse } from '../../utils/DataAccessUtils';
import { getRecordsForTargetCollection } from '../../reducers/standard_reducer_utils';
import { DateTimeWithTimeZonev4 } from '../date_time_with_time_zone/DateTimeWithTimeZonev4';
import { wallTimeToUtcIso, BE_DATE_ONLY_FORMAT } from '../../utils/DataFormatterUtils';
import { ROLE_TYPES } from '../../utils/DataConstants';

const LOC_TC_NAME = 'scheduledTransferDropdown';
const AREA_TC_NAME = 'scheduledTransferDropdown';
const FORM_ID = 'bulk-transfer-location-form';

const BulkTransferToLocationModal: FC<any> = ({
	assetIds,
	transferId,
	initialValues,
	currentLocationId,
	onCancel,
	onSuccess,
	locations,
	areas,
	companyConfig,
	fetchLocations,
	fetchMultipleLocations,
	fetchAreas,
	fetchMultipleAreas,

	scheduleTransfer,
	updateTransfer,
	bulkTransferAssets,
	moveAssetToLocation,
	cancelTransfer,
}): React.ReactElement => {
	const [form] = Form.useForm();
	const [saving, setSaving] = useState(false);

	const transferNoteRequired = useMemo(
		() => nullSafeGetOrElse('inventoryConfig.transferNoteMandatory', companyConfig, false),
		[companyConfig]
	);

	const selectedLocationId = Form.useWatch('locationId', form);
	const triggerAtValue = Form.useWatch('triggerAt', form);

	const selectedLocation = useMemo(() => {
		if (!selectedLocationId) return null;
		const records = getRecordsForTargetCollection(locations, LOC_TC_NAME);
		return records.find((_) => _.id === selectedLocationId) || null;
	}, [selectedLocationId, locations]);

	const isFutureDate = useMemo(
		() => triggerAtValue && moment(triggerAtValue).isAfter(moment()),
		[triggerAtValue]
	);

	// Reset area whenever the location changes
	useEffect(() => {
		if (!selectedLocationId) return;
		form.setFieldsValue({ areaId: undefined });
	}, [selectedLocationId, form]);

	const handleSubmit = useCallback(
		(values) => {
			setSaving(true);

			if (!isFutureDate) {
				const transferDate = values.triggerAt
					? moment.utc(values.triggerAt).format(BE_DATE_ONLY_FORMAT)
					: undefined;
				let promise;
				if (assetIds.length === 1) {
					const data = {
						locationId: values.locationId,
						...(values.areaId && { areaId: values.areaId }),
						...(values.transferNote && { transferNote: values.transferNote }),
						...(transferDate && { transferDate }),
					};
					promise = moveAssetToLocation(assetIds[0], data);
				} else {
					const entities = assetIds.map((assetId) => ({
						assetId: `${assetId}`,
						locationId: values.locationId,
						transferDate,
						...(values.areaId && { areaId: values.areaId }),
						...(values.transferNote && { transferNote: values.transferNote }),
					}));
					promise = bulkTransferAssets(entities);
				}

				promise
					.then(() => (transferId ? cancelTransfer({ assetIds }) : Promise.resolve()))
					.then(onSuccess)
					.catch(message.error)
					.finally(() => setSaving(false));
				return;
			}

			const triggerAt =
				values.triggerAt &&
				wallTimeToUtcIso(values.triggerAt, values.transferTimezone || moment.tz.guess());
			const entity = {
				triggerAt,
				assetIds,
				locationId: values.locationId,
				...(values.areaId && { areaId: values.areaId }),
				...(values.transferNote && { transferNote: values.transferNote }),
			};
			const promise = transferId ? updateTransfer(entity) : scheduleTransfer(entity);

			promise
				.then(onSuccess)
				.catch(message.error)
				.finally(() => setSaving(false));
		},
		[
			assetIds,
			isFutureDate,
			transferId,
			scheduleTransfer,
			updateTransfer,
			bulkTransferAssets,
			moveAssetToLocation,
			cancelTransfer,
			onSuccess,
		]
	);

	return (
		<Modal
			visible={true}
			title={transferId ? 'Edit scheduled transfer' : 'Transfer to location'}
			onCancel={onCancel}
			footer={null}
			closable={true}
		>
			<Form
				initialValues={{
					...initialValues,
					triggerAt: nullSafeGet('triggerAt', initialValues)
						? moment(initialValues.triggerAt).startOf('hour')
						: moment().startOf('hour'),
				}}
				id={FORM_ID}
				layout="vertical"
				form={form}
				onFinish={handleSubmit}
			>
				<Form.Item
					name="locationId"
					label="Location"
					rules={[{ required: true, message: 'Location is required!' }]}
				>
					<OWAsyncSelect
						stateSlice={locations}
						targetCollectionName={LOC_TC_NAME}
						fetchMultiple={fetchMultipleLocations}
						fetchData={(searchText, targetCollectionName) =>
							fetchLocations({ name: searchText }, targetCollectionName)
						}
						renderRecord={(record) => (
							<Select.Option
								key={record.id}
								value={record.id}
								disabled={record.id === currentLocationId}
							>
								{record.name}
							</Select.Option>
						)}
					/>
				</Form.Item>
				<Form.Item name="areaId" label="Area">
					<OWAsyncSelect
						disabled={!selectedLocationId}
						stateSlice={areas}
						targetCollectionName={AREA_TC_NAME}
						fetchMultiple={fetchMultipleAreas}
						fetchData={(searchText, targetCollectionName) =>
							fetchAreas({ name: searchText, locationId: selectedLocationId }, targetCollectionName)
						}
						renderRecord={(record) => (
							<Select.Option key={record.id} value={record.id}>
								{record.name}
							</Select.Option>
						)}
						additionalFilters={{ locationId: selectedLocationId }}
					/>
				</Form.Item>
				<div className="mb-6">
					<DateTimeWithTimeZonev4
						form={form}
						location={selectedLocation}
						displayName="Effective transfer date"
						dateName="triggerAt"
						timeZoneName="transferTimezone"
						rules={[{ required: true, message: 'Transfer date is required.' }]}
						help={
							isFutureDate ? (
								<span>Future-timed transfers will be executed on the chosen date and time.</span>
							) : null
						}
						showTime={{ format: 'h A', showMinute: false, showSecond: false }}
						initialTimeZone={selectedLocation ? selectedLocation.timeZoneId : undefined}
						required
						showNow={false}
					/>
				</div>
				<Form.Item
					name="transferNote"
					label="Transfer Note"
					rules={[{ required: transferNoteRequired, message: 'Please enter transfer note.' }]}
				>
					<TextArea autoSize={{ minRows: 3 }} />
				</Form.Item>
				{isFutureDate ? (
					<Form.Item
						name="consent"
						rules={[
							{
								validator: (_, value) =>
									value ? Promise.resolve() : Promise.reject(new Error('Please confirm')),
							},
						]}
						valuePropName="checked"
					>
						<Checkbox>
							<span className="text-sunsetOrange-8">
								I understand that this is a future-dated transfer and it will be executed on the
								chosen date and time.
							</span>
						</Checkbox>
					</Form.Item>
				) : null}
				<ModalFormFooter
					okText={isFutureDate ? 'Schedule Transfer' : 'Transfer'}
					cancelText="Cancel"
					loading={saving}
					formId={FORM_ID}
					onCancelClick={onCancel}
				/>
			</Form>
		</Modal>
	);
};

const mapStateToProps = (state) => ({
	locations: state.locations,
	areas: state.areas,
	companyConfig: state.company_config.detail,
});

const mapDispatchToProps = (dispatch) => ({
	fetchLocations: (params, targetCollectionName) =>
		dispatch(locationsRestCrudThunksForSupplier.readLite(params, targetCollectionName)),
	fetchMultipleLocations: (ids, targetCollectionName) =>
		dispatch(locationsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
	fetchAreas: (params, targetCollectionName) =>
		dispatch(areasRestCrudThunksForSupplier.readLite(params, targetCollectionName)),
	fetchMultipleAreas: (ids, targetCollectionName) =>
		dispatch(areasRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)),
	scheduleTransfer: (data) => dispatch(scheduleAssetTransfer(ROLE_TYPES.SUPPLIER)(data)),
	updateTransfer: (data) => dispatch(updateScheduledAssetTransfer(ROLE_TYPES.SUPPLIER)(data)),
	bulkTransferAssets: (entities) => dispatch(bulkTransferAssetsToLocationsForSupplier(entities)),
	moveAssetToLocation: (assetId, data) => dispatch(moveAssetToLocationForSupplier(assetId, data)),
	cancelTransfer: (data) => dispatch(cancelScheduledAssetTransfer(ROLE_TYPES.SUPPLIER)(data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(BulkTransferToLocationModal);
